import React from 'react';
import { Action } from '../components/v2BaseComponents/ActionButtonLayout/types';
import { getIntl } from '../utils/intl';
import { usePromptButton } from '../components/shared/PromptButton';
import Text from '../components/v2BaseComponents/Text';
import { PromptActions } from '../components/v2BaseComponents/Prompt';

const intl = getIntl('useListEditActions', {
    add: 'Add',
    link: 'Link',
    cancel: 'Cancel',
    save: 'Save',
    promptHeader: 'Are You Sure?',
    promptContentPrefix: 'This will {content}',
    yes: 'Yes',
});

export type ListMode = 'related' | 'link';

export type UseListEditActionsArgs = {
    canEdit: boolean;
    listMode: ListMode;
    onEdit: () => void;
    onCancel: () => void;
    onAdd: () => void;
    onSubmitLinkUpdates: () => void;
    isDisabled: boolean;
    promptChangeDescription: string;
};

export type UseListEditActionsReturnValue = {
    actions: Action[];
    Prompt: React.ReactNode;
};

export const useListEditActions = ({
    canEdit,
    listMode,
    onEdit,
    onCancel,
    onAdd,
    onSubmitLinkUpdates,
    isDisabled,
    promptChangeDescription,
}: UseListEditActionsArgs): UseListEditActionsReturnValue => {
    const { Prompt, onPress } = usePromptButton({
        renderPromptContents: (onClose) => (
            <>
                <Text variant="body-1">{intl('promptContentPrefix', { content: promptChangeDescription })}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('yes'),
                            onPress: () => {
                                onSubmitLinkUpdates();
                                onClose();
                            },
                            icon: 'CheckCircle',
                            testID: 'link-prompt-yes-button',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            variant: 'secondary',
                            icon: 'XCircle',
                            testID: 'link-prompt-no-button',
                        },
                    ]}
                />
            </>
        ),
        promptTitle: intl('promptHeader'),
    });
    const actions: Action[] = React.useMemo<Action[]>(() => {
        const returnValue: Action[] = [];
        if (!canEdit) return returnValue;

        if (listMode === 'related') {
            returnValue.push(
                {
                    icon: 'PlusCircle',
                    title: intl('add'),
                    onPress: onAdd,
                    testID: 'list-add-button',
                    show: !isDisabled,
                },
                {
                    icon: 'Link',
                    title: intl('link'),
                    onPress: onEdit,
                    testID: 'prompt-link-button',
                    show: !isDisabled,
                },
            );
        } else {
            returnValue.push(
                {
                    icon: 'X',
                    title: intl('cancel'),
                    onPress: onCancel,
                    testID: 'cancel-link-button',
                    show: !isDisabled,
                    variant: 'secondary',
                },
                {
                    icon: 'Check',
                    title: intl('save'),
                    onPress: onPress,
                    testID: 'save-link-button',
                    show: !isDisabled,
                },
            );
        }

        return returnValue;
    }, [canEdit, listMode, onAdd, isDisabled, onEdit, onCancel, onPress]);

    return {
        actions,
        Prompt: Prompt,
    };
};
